import type { Router } from 'vue-router';

import * as Sentry from '@sentry/browser';
import { OpenApiError } from '@zyno-io/openapi-client-codegen';
import { uxrr } from '@zyno-io/uxrr-client';
import { ref, watch } from 'vue';

import type { IIdentityResponse, IWorkstationListItem, IZynoPermissions } from './openapi-clients-generated/auth';
import type { ISalesCommonConfigResponse } from './openapi-clients-generated/sales';
import type { ICommonConfigGetResponse } from './openapi-clients-generated/suite';

import { SharedState, activeTenantIdRef, crmPrimaryContactTypeRef, isWorkstationRef } from './global.shared';
import { ContactResolution } from './modules/crm/exports/contact-resolution';
import { TalkTenantApi } from './openapi-clients-generated/talk';
import { OtelState, syncIdentity } from './otel';
import { DataCache } from './shared/data/cache';
import { SSEService } from './shared/services/sse';

export type ITenant = IIdentityResponse['tenants'][number];
export type ITenantConfiguration = ICommonConfigGetResponse & ISalesCommonConfigResponse;

class State extends SharedState {
    router!: Router;
    syncState: { workstationId?: string; userId?: string; tenantId?: string } = {};

    // Test hook: VRT seeds window.__zs_vrt.workstation to force workstation-mode rendering
    // (enables Inventory print/scan-and-print row actions, sidebar workstation branch, etc.).
    workstation = ref<Pick<IWorkstationListItem, 'id'> | null>(
        ((typeof window !== 'undefined' &&
            (window as unknown as { __zs_vrt?: { workstation?: Pick<IWorkstationListItem, 'id'> } }).__zs_vrt?.workstation) ||
            null) ??
            (import.meta.env.VITE_APP_AUTH_WID ? { id: import.meta.env.VITE_APP_AUTH_WID } : null)
    );
    user = ref<IIdentityResponse | null>(null);
    tenant = ref<Pick<ITenant, 'id' | 'name' | 'isAdmin' | 'mspId'> | null>(null);
    permissions = ref<IZynoPermissions | null>(null);
    // Test hook: VRT seeds window.__zs_vrt.mobileApp to force in-app (native
    // webview) rendering — hides the web sidebar and applies the mobile shell.
    enableMobileAppDesign = ref(
        (typeof window !== 'undefined' && !!(window as unknown as { __zs_vrt?: { mobileApp?: boolean } }).__zs_vrt?.mobileApp) || false
    );
    tenantConfiguration = ref<ITenantConfiguration | null>(null);

    // Whether the active tenant is provisioned for Talk (GET /tenant): true/false once known, null
    // while unknown. Gates Talk requests/UI that live outside the Talk module.
    private _talkProvisioned = ref<boolean | null>(null);
    private _talkProbe?: Promise<boolean>;

    constructor() {
        super();
        watch([this.workstation, this.user, this.tenant], () => this.syncStates(), { immediate: true });
        // Mirror the CRM primary-contact-type into the lean shared ref so cross-module helpers can
        // read it without importing this module. See global.shared.ts.
        watch(
            this.tenantConfiguration,
            () => {
                crmPrimaryContactTypeRef.value = this.tenantConfiguration.value?.['crm.primaryContactType'] ?? null;
            },
            { immediate: true }
        );
    }

    get isWorkstation() {
        return this.workstation.value !== null;
    }

    /** Whether the active tenant is provisioned for Talk: true/false once known, null while unknown. */
    get talkProvisioned(): boolean | null {
        return this._talkProvisioned.value;
    }

    /** Probe GET /tenant once per tenant; used to gate Talk requests that fire outside the Talk module. */
    ensureTalkProvisioned(): Promise<boolean> {
        return (this._talkProbe ??= TalkTenantApi.getTenantGetTenant({ meta: { ignoreSentry404: true } }).then(
            () => {
                this._talkProvisioned.value = true;
                return true;
            },
            (err: unknown) => {
                // 404 = this tenant has no Talk: a definitive answer worth memoizing — skip Talk requests.
                const is404 = err instanceof OpenApiError && err.response?.status === 404;
                this._talkProvisioned.value = is404 ? false : null;
                if (is404) return false;
                // Any other failure (network/5xx) leaves provisioning unknown. DON'T memoize it: clear the
                // probe so the next call (e.g. a "Try again" retry) re-probes GET /tenant, and propagate the
                // error so Talk surfaces show a retryable error instead of a permanently-cached failure.
                this._talkProbe = undefined;
                throw err;
            }
        ));
    }

    syncStates() {
        // Mirror workstation-ness and the active tenant id into the lean shared refs so the Loader,
        // sw.manager and other lean code can read them without importing this module. See global.shared.ts.
        isWorkstationRef.value = this.isWorkstation;
        activeTenantIdRef.value = this.tenant.value?.id ?? null;

        this.syncTalkProvisioningState();
        this.syncCacheState();
        this.syncSSEState();
        this.syncSentryState();
        this.syncOtelState();

        this.syncState = {
            workstationId: this.workstation.value?.id,
            userId: this.user.value?.id,
            tenantId: this.tenant.value?.id
        };
    }

    syncTalkProvisioningState() {
        // Only re-probe when the active user/tenant actually changed (mirrors syncSSEState).
        if (this.syncState.userId === this.user.value?.id && this.syncState.tenantId === this.tenant.value?.id) return;

        this._talkProbe = undefined;
        this._talkProvisioned.value = null;
        if (this.user.value && this.tenant.value) {
            // Kick the probe eagerly so Talk UI (e.g. the location Talk tab) can hide before any
            // Talk cache loader runs; the cache gate awaits this same memoized probe.
            this.ensureTalkProvisioned().catch(() => {});
        }
    }

    syncCacheState() {
        // Phone→contact resolutions are tenant-scoped and not part of DataCache, so they're cleared
        // on the same beat the caches reload — including on sign-out, where there is no user left to
        // reload for but the previous tenant's contact names must not survive into the next session.
        ContactResolution.reset();
        if (this.user.value) {
            DataCache.reloadCaches();
        }
    }

    syncSSEState() {
        if (!this.user.value || !this.tenant.value) {
            SSEService.disconnect();
        } else if (this.syncState.userId !== this.user.value?.id) {
            SSEService.connect();
        } else if (this.syncState.tenantId !== this.tenant.value?.id) {
            SSEService.connect();
        }
    }

    syncSentryState() {
        Sentry.setUser(
            this.user.value
                ? {
                      id: this.user.value.id,
                      email: this.user.value.email
                  }
                : null
        );
        Sentry.setTag('uxrrSessionId', uxrr.sessionId);
    }

    syncOtelState() {
        OtelState.workstationId = this.workstation.value?.id ?? `b/${this.browserId}`;
        OtelState.userId = this.user.value?.id;
        OtelState.userName = this.user.value ? `${this.user.value.firstName} ${this.user.value.lastName}`.trim() : undefined;
        OtelState.userEmail = this.user.value?.email;
        OtelState.tenantId = this.tenant.value?.id;
        syncIdentity();
    }
}

export const GlobalState = new State();

// Gate the Talk requests that DataCache fires from non-Talk surfaces (e.g. the QAB phone icon) on
// the active tenant's Talk provisioning, so they don't run for tenants without Talk.
DataCache.talkProvisioningGate = () => GlobalState.ensureTalkProvisioned();
